import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

// ---------- Types ----------
export interface GuestRating {
  guestId: string;
  reservationId?: string;
  listingRating: number;
  dateCreated: string;
  cleanliness: number;
  location: number;
  communication: number;
  accuracy: number;
  service: number;
  overallRating:
    | "Excellent"
    | "Superb"
    | "Very Nice"
    | "Worth It"
    | "Not Worth It";
  guestReview: string;
  checkInRating?: number;
  communicationGeneral?: number;
  value?: number;
}

export interface Summary {
  avgRating: number;
  cleanliness: number;
  location: number;
  communication: number;
  accuracy: number;
  service: number;
  value: number;
  totalReviews: number;
  lastUpdated: string;
}

export interface ShortRatingState {
  ratings: Record<string, GuestRating[]>; // keyed by listingId
  loading: boolean;
  error: string | null;
  reviewed: boolean;
  currentReview?: GuestRating | null;
    summaries: Record<string, Summary | null>;
}

const initialState: ShortRatingState = {
  ratings: {},
  loading: false,
  error: null,
  reviewed: false,
  currentReview: null,
summaries: {},
};

// ---------- Submit a Rating (includes summary updates) ----------
export const submitShortRating = createAsyncThunk(
  "shortRatings/submitShortRating",
  async (
    {
      listingId,
      hostId,
      reservationId,
      guestId,
      reviewText,
      overallRating,
      criteria,
      listingRating,
    }: {
      listingId: string;
      hostId: string;
      reservationId: string;
      guestId: string;
      reviewText: string;
      overallRating: string;
      listingRating: number;
      criteria: Record<string, number>;
    },
    { rejectWithValue }
  ) => {
    try {
      // 1️⃣ Prepare guest review doc reference
      const guestRef = doc(db, "shortratings", listingId, "ratings", guestId);
      const guestSnap = await getDoc(guestRef);

      const newRating: GuestRating = {
        reservationId,
        listingRating,
        guestId,
        guestReview: reviewText,
        overallRating: overallRating as GuestRating["overallRating"],
        cleanliness: criteria["Cleanliness"] || 0,
        location: criteria["Location"] || 0,
        communication: criteria["Host Communication"] || 0,
        accuracy: criteria["Accuracy"] || 0,
        service: criteria["Host Service"] || 0,
        checkInRating: criteria["Check-in"] || 0,
        communicationGeneral: criteria["Communication"] || 0,
        value: criteria["Value"] || 0,
        dateCreated: new Date().toISOString(),
      };

      // 2️⃣ Save rating under guest
      if (guestSnap.exists()) {
        const existing = guestSnap.data();
        const ratings: GuestRating[] = existing.ratings || [];
        const alreadyReviewed = ratings.some(
          (r) => r.reservationId === reservationId
        );
        if (alreadyReviewed) return { reviewed: true, listingId };

        await updateDoc(guestRef, {
          ratings: [...ratings, newRating],
          lastUpdated: Timestamp.now(),
        });
      } else {
        await setDoc(guestRef, {
          guestId,
          ratings: [newRating],
          createdAt: Timestamp.now(),
        });
      }

      // 3️⃣ Update summary for listing (incremental)
      const listingRef = doc(db, "shortratings", listingId);
      const listingSnap = await getDoc(listingRef);

      const summary: Summary = listingSnap.data()?.summary || {
        avgRating: 0,
        cleanliness: 0,
        location: 0,
        communication: 0,
        accuracy: 0,
        service: 0,
        value: 0,
        totalReviews: 0,
        lastUpdated: new Date().toISOString(),
      };

      const n = summary.totalReviews;
      const newN = n + 1;

  const updatedSummary: Summary = {
  avgRating:
    (summary.avgRating * n + (newRating.listingRating ?? 0)) / newN,
  cleanliness:
    (summary.cleanliness * n + (newRating.cleanliness ?? 0)) / newN,
  location:
    (summary.location * n + (newRating.location ?? 0)) / newN,
  communication:
    (summary.communication * n + (newRating.communication ?? 0)) / newN,
  accuracy:
    (summary.accuracy * n + (newRating.accuracy ?? 0)) / newN,
  service:
    (summary.service * n + (newRating.service ?? 0)) / newN,
  value:
    (summary.value * n + (newRating.value ?? 0)) / newN,
  totalReviews: newN,
  lastUpdated: new Date().toISOString(),
};


      // 4️⃣ Merge summary + ensure listing root exists
      if (listingSnap.exists()) {
        await updateDoc(listingRef, { summary: updatedSummary });
      } else {
        await setDoc(listingRef, {
          listingId,
          hostId,
          summary: updatedSummary,
          createdAt: Timestamp.now(),
        });
      }

      return {
        reviewed: false,
        listingId,
        guestId,
        rating: newRating,
        summary: updatedSummary,
      };
    } catch (err: any) {
      console.error("🔥 submitShortRating error:", err);
      return rejectWithValue(err.message);
    }
  }
);

// ---------- Fetch all ratings for a listing ----------
export const fetchShortRatingsByListing = createAsyncThunk<
  { listingId: string; ratings: GuestRating[] },
  string,
  { rejectValue: string }
>("shortRatings/fetchByListing", async (listingId, { rejectWithValue }) => {
  try {
    const ratingsCol = collection(db, "shortratings", listingId, "ratings");
    const snapshot = await getDocs(ratingsCol);

    const allRatings: GuestRating[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.ratings) allRatings.push(...data.ratings);
    });

    return { listingId, ratings: allRatings };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------- Fetch ratings for a specific guest ----------
export const fetchGuestRatings = createAsyncThunk<
  { listingId: string; guestId: string; ratings: GuestRating[] },
  { listingId: string; guestId: string },
  { rejectValue: string }
>("shortRatings/fetchGuestRatings", async ({ listingId, guestId }, { rejectWithValue }) => {
  try {
    const docRef = doc(db, "shortratings", listingId, "ratings", guestId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return { listingId, guestId, ratings: [] };
    return { listingId, guestId, ratings: snap.data().ratings || [] };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------- Fetch rating by reservationId ----------
export const fetchRatingByReservation = createAsyncThunk<
  GuestRating | null,
  { listingId: string; reservationId: string },
  { rejectValue: string }
>("shortRatings/fetchByReservation", async ({ listingId, reservationId }, { rejectWithValue }) => {
  try {
    const ratingsCol = collection(db, "shortratings", listingId, "ratings");
    const snapshot = await getDocs(ratingsCol);

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const match = data.ratings?.find(
        (r: any) => r.reservationId === reservationId
      );
      if (match) return match;
    }

    return null;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------- Fetch all ratings across listings ----------
export const fetchAllShortRatings = createAsyncThunk(
  "shortRatings/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const listingsSnap = await getDocs(collection(db, "shortratings"));
      const allRatings: Record<string, GuestRating[]> = {};

      for (const listingDoc of listingsSnap.docs) {
        const listingId = listingDoc.id;
        const ratingsCol = collection(db, "shortratings", listingId, "ratings");
        const ratingDocs = await getDocs(ratingsCol);

        const listingRatings: GuestRating[] = [];
        ratingDocs.forEach((rDoc) => {
          const data = rDoc.data();
          if (data.ratings) listingRatings.push(...data.ratings);
        });

        allRatings[listingId] = listingRatings;
      }

      return allRatings;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchAllShortRatingSummaries = createAsyncThunk<
  Record<string, Summary | null>,
  void,
  { rejectValue: string }
>("shortRatings/fetchAllSummaries", async (_, { rejectWithValue }) => {
  try {
    const listingsSnap = await getDocs(collection(db, "shortratings"));
    const summaries: Record<string, Summary | null> = {};

    listingsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      summaries[docSnap.id] = data.summary || null;
    });

    return summaries;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});


// ---------- Slice ----------
const shortRatingSlice = createSlice({
  name: "shortRatings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Submit
      .addCase(submitShortRating.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitShortRating.fulfilled, (state, action) => {
        const { listingId, rating } = action.payload;
        if (!state.ratings[listingId]) state.ratings[listingId] = [];
        if (rating) state.ratings[listingId].push(rating);
        state.loading = false;
      })
      .addCase(submitShortRating.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })

      // Fetch listing ratings
      .addCase(fetchShortRatingsByListing.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchShortRatingsByListing.fulfilled, (state, action) => {
        state.ratings[action.payload.listingId] = action.payload.ratings;
        state.loading = false;
      })
      .addCase(fetchShortRatingsByListing.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })

      // Fetch guest-specific
      .addCase(fetchGuestRatings.fulfilled, (state, action) => {
        state.ratings[action.payload.listingId] = action.payload.ratings;
      })

      // Fetch by reservation
      .addCase(fetchRatingByReservation.fulfilled, (state, action) => {
        state.currentReview = action.payload;
      })

      // Fetch all
      .addCase(fetchAllShortRatings.fulfilled, (state, action) => {
        state.ratings = action.payload;
      })
      .addCase(fetchAllShortRatingSummaries.fulfilled, (state, action) => {
  state.summaries = action.payload;
});

  },
});

export default shortRatingSlice.reducer;
